import { useState } from 'react';
import type { TreeNode } from '../hooks/useTree';

interface NodeProps {
  node: TreeNode;
  currentPrefix: string;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  onNavigate: (path: string) => void;
  onDrop?: (targetPath: string, sourcePaths: string[]) => void;
  onContextMenu?: (e: React.MouseEvent, path: string, name: string) => void;
  depth: number;
}

function TreeNodeItem({ node, currentPrefix, expanded, onToggle, onNavigate, onDrop, onContextMenu, depth }: NodeProps) {
  const isExpanded  = expanded.has(node.path);
  const isActive    = currentPrefix === node.path;
  const isInPath    = currentPrefix.startsWith(node.path + '/');
  const hasChildren = !node.loaded || (node.children && node.children.length > 0);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      const data = e.dataTransfer.getData('application/json');
      if (data && onDrop) {
        const sourcePaths = JSON.parse(data);
        onDrop(node.path, sourcePaths);
      }
    } catch (err) {
      console.error('Error handling drop:', err);
    }
  };

  return (
    <li className="list-unstyled mb-0">
      <div
        className={`d-flex align-items-center px-2 py-1 tree-node ${isActive ? 'active' : ''} ${isInPath ? 'in-path' : ''} ${isDragOver ? 'drag-over' : ''}`}
        style={{ 
          cursor: 'pointer', 
          paddingLeft: `${depth * 20 + 8}px`, 
          fontSize: '13px', 
          userSelect: 'none',
          position: 'relative',
          borderLeft: depth > 0 ? '2px solid #e2e8f0' : 'none',
          marginLeft: depth > 0 ? `${(depth - 1) * 20 + 8}px` : '0'
        }}
        onClick={() => onNavigate(node.path)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onContextMenu) onContextMenu(e, node.path, node.name);
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Línea de conexión horizontal */}
        {depth > 0 && (
          <span
            style={{
              position: 'absolute',
              left: '-2px',
              top: '50%',
              width: '10px',
              height: '2px',
              background: '#e2e8f0'
            }}
          />
        )}

        {/* Expand toggle */}
        <span
          className="me-1"
          style={{ width: 18, flexShrink: 0, fontSize: 11, opacity: hasChildren ? 1 : 0, fontWeight: 600 }}
          onClick={e => { e.stopPropagation(); onToggle(node.path); }}
        >
          {isExpanded ? '▼' : '▶'}
        </span>

        {/* Folder icon */}
        <i 
          className={`bi ${isExpanded ? 'bi-folder2-open' : 'bi-folder'} me-2`} 
          style={{ 
            fontSize: 15, 
            color: isActive || isInPath ? '#2563eb' : '#94a3b8'
          }} 
        />

        {/* Name */}
        <span className="text-truncate" style={{ fontWeight: isActive ? 600 : isInPath ? 500 : 400 }}>
          {node.name}
        </span>

        {/* Indicador de ubicación actual */}
        {isActive && (
          <i className="bi bi-geo-alt-fill ms-auto" style={{ fontSize: 12, color: '#2563eb' }} />
        )}
      </div>

      {/* Children */}
      {isExpanded && node.children && node.children.length > 0 && (
        <ul className="list-unstyled mb-0">
          {node.children.map(child => (
            <TreeNodeItem
              key={child.path}
              node={child}
              currentPrefix={currentPrefix}
              expanded={expanded}
              onToggle={onToggle}
              onNavigate={onNavigate}
              onDrop={onDrop}
              onContextMenu={onContextMenu}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

interface Props {
  roots: TreeNode[];
  currentPrefix: string;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  onNavigate: (path: string) => void;
  onDrop?: (targetPath: string, sourcePaths: string[]) => void;
  onNewFolder?: () => void;
  onContextMenu?: (e: React.MouseEvent, path: string, name: string) => void;
}

export default function DirectoryTree({ roots, currentPrefix, expanded, onToggle, onNavigate, onDrop, onNewFolder, onContextMenu }: Props) {
  const [isDragOverRoot, setIsDragOverRoot] = useState(false);

  const handleRootDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverRoot(true);
  };

  const handleRootDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverRoot(false);
  };

  const handleRootDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverRoot(false);

    try {
      const data = e.dataTransfer.getData('application/json');
      if (data && onDrop) {
        const sourcePaths = JSON.parse(data);
        onDrop('', sourcePaths);
      }
    } catch (err) {
      console.error('Error handling root drop:', err);
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      {/* Header con botón Nueva carpeta */}
      <div className="p-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
        {onNewFolder && (
          <button 
            className="btn btn-sm btn-feebbo w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={onNewFolder}
            style={{ fontSize: '13px' }}
          >
            <i className="bi bi-folder-plus" />
            Nueva carpeta
          </button>
        )}
      </div>

      {/* Árbol de navegación */}
      <div className="flex-grow-1 overflow-auto">
        <div className="p-2">
          {/* Root - Mi unidad */}
          <div
            className={`d-flex align-items-center px-2 py-1 mb-2 tree-node ${!currentPrefix ? 'active' : ''} ${isDragOverRoot ? 'drag-over' : ''}`}
            style={{ 
              cursor: 'pointer', 
              fontSize: '14px', 
              userSelect: 'none',
              fontWeight: 600,
              borderRadius: '6px'
            }}
            onClick={() => onNavigate('')}
            onDragOver={handleRootDragOver}
            onDragLeave={handleRootDragLeave}
            onDrop={handleRootDrop}
          >
            <i className="bi bi-house-door-fill me-2" style={{ fontSize: 16, color: !currentPrefix ? '#2563eb' : '#64748b' }} />
            <span>Mi unidad</span>
            {!currentPrefix && (
              <i className="bi bi-geo-alt-fill ms-auto" style={{ fontSize: 12, color: '#2563eb' }} />
            )}
          </div>

          {/* Árbol de carpetas */}
          <ul className="list-unstyled mb-0">
            {roots.map(node => (
              <TreeNodeItem
                key={node.path}
                node={node}
                currentPrefix={currentPrefix}
                expanded={expanded}
                onToggle={onToggle}
                onNavigate={onNavigate}
                onDrop={onDrop}
                onContextMenu={onContextMenu}
                depth={0}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
